import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import type { AdminApi } from "@/api";
import type { CauseLoopTarget, CauseLoopTargetList, ReferenceScoreResult } from "@/types";
import { Badge, Banner, Button, Card, Checkbox } from "@/components/ui";
import { cn } from "@/lib/utils";

/** How many TCs one ref 채점 request judges (the server's own limit). */
const MAX_SCORE = 3;

const STATE_LABEL: Record<string, string> = { unresolved: "미해결", resolved: "해결", deferred: "보류" };
const STALE_LABEL: Record<string, string> = { criteria_changed: "기준 바뀜" };
/** What a ref 채점 outcome code means, said plainly; an unknown code is shown as it is. */
const OUTCOME_LABEL: Record<string, string> = {
  scored: "채점함",
  already_scored: "이미 채점됨",
  not_eligible: "대상 아님",
  reference_origin_missing: "Reference를 만든 run을 모름",
  reference_origin_mismatch: "Reference 답이 run 답과 다름",
  trace_missing: "run 기록을 읽지 못함",
  judge_failed: "판정 실패",
  CAUSE_LOOP_PROD_ACCESS_REQUIRED: "prod 권한 없음",
  failed: "실패",
};

function score(value: number | null) {
  return value == null ? "—" : value.toFixed(2);
}

function why(item: CauseLoopTarget) {
  if (item.stale) return `${item.stale_reasons.map((r) => STALE_LABEL[r] ?? r).join(", ")} (다시 채점하지 않음)`;
  if (!item.measured) return "ref 미채점";
  if (item.gap == null) return "TC 점수 없음";
  return null;
}

function summary(results: ReferenceScoreResult[]) {
  const counts = new Map<string, number>();
  for (const r of results) counts.set(r.outcome, (counts.get(r.outcome) ?? 0) + 1);
  return [...counts].map(([outcome, n]) => `${OUTCOME_LABEL[outcome] ?? outcome} ${n}개`).join(", ");
}

/**
 * Test Cases → 루프 대상. Active TCs whose Reference a person passed, by score
 * gap (the Reference run's v3 score − the TC's newest v3 score, computed when
 * the list is read). Picking rows only selects them; "ref 채점" judges the
 * picked TCs' Reference answers once (up to 3, in the request) and reads the
 * list again. A loop is still started from MCP / CLI, never from this screen.
 * Each row opens its Reference review (the TC id) or its Test Result.
 */
export function CauseLoopTargetsPage({
  api,
  onOpen,
  onOpenResult,
}: {
  api: AdminApi;
  /** Opens the TC on the Reference review screen. */
  onOpen?: (testCaseId: string) => void;
  /** Opens the TC's results in Test Result. */
  onOpenResult?: (testCaseId: string) => void;
}) {
  const [list, setList] = useState<CauseLoopTargetList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<ReferenceScoreResult[] | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setList(await api.listCauseLoopTargets({ limit: 30 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function more() {
    if (!list?.next_cursor) return;
    setLoadingMore(true);
    try {
      const next = await api.listCauseLoopTargets({ cursor: list.next_cursor, limit: 30 });
      setList({ items: [...list.items, ...next.items], next_cursor: next.next_cursor });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  function toggle(id: string) {
    setPicked((now) => {
      const next = new Set(now);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function start() {
    setStarting(true);
    setStartError(null);
    setResults(null);
    try {
      const out = await api.scoreReferences([...picked]);
      setResults(out.results);
      setPicked(new Set());
      void load();
    } catch (e) {
      setStartError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  const ready = picked.size > 0 && picked.size <= MAX_SCORE;
  const items = list?.items ?? [];

  return (
    <div className="grid gap-4.5">
      {error && <Banner tone="error">루프 대상을 읽지 못했습니다: {error}</Banner>}
      <Card className="grid min-w-0 gap-2.5 p-3.5">
        <div className="text-[13px] font-medium">ref 채점</div>
        <p className="text-[12px] text-muted-foreground">
          고른 TC마다 사람이 통과시킨 Reference 답을 지금 기준(v3, Reference 없이)으로 한 번 채점해 그 run에 남깁니다.
          이미 채점된 것은 다시 채점하지 않습니다. 점수 차이는 그 점수에서 TC의 최신 v3 점수를 빼서 목록을 읽을 때
          계산합니다. TC 하나에 판정 1회가 듭니다.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!ready || starting} onClick={() => void start()}>
            {starting && <Loader2 className="animate-spin" />}ref 채점 ({picked.size}개)
          </Button>
        </div>
        {picked.size > MAX_SCORE && (
          <div className="text-[12px] text-destructive">한 번에 {MAX_SCORE}개까지 채점합니다.</div>
        )}
        {startError && <Banner tone="error">ref 채점을 하지 못했습니다: {startError}</Banner>}
        {results && (
          <div role="status" className="text-[12px] text-muted-foreground">
            ref 채점 결과: {summary(results)}
          </div>
        )}
      </Card>
      <Card className="min-w-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border text-left text-[11.5px] text-muted-foreground">
                <th className="w-8 px-2.5 py-2" />
                <th className="px-2.5 py-2 font-medium">TC</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap">점수 차이</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap">Reference / TC</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap">상태</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap">시도</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap max-md:hidden">Reference 버전</th>
                <th className="px-2.5 py-2 font-medium whitespace-nowrap max-md:hidden">ref 채점</th>
              </tr>
            </thead>
            <tbody>
              {!list && !error && (
                <tr>
                  <td colSpan={8} className="px-3 py-5 text-muted-foreground">
                    <Loader2 className="mr-1.5 inline size-4 animate-spin" />
                    목록을 읽는 중…
                  </td>
                </tr>
              )}
              {list && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-5 text-muted-foreground">
                    루프 대상이 없습니다. 활성 TC의 Reference를 사람이 통과시키면 여기에 나옵니다.
                  </td>
                </tr>
              )}
              {items.map((item) => {
                const note = why(item);
                return (
                  <tr key={item.test_case_id} data-testid="cause-loop-target" className="border-b border-border align-top last:border-0">
                    <td className="px-2.5 py-2">
                      <Checkbox
                        aria-label={`${item.test_case_id.slice(0, 8)} 고르기`}
                        checked={picked.has(item.test_case_id)}
                        onCheckedChange={() => toggle(item.test_case_id)}
                      />
                    </td>
                    <td className="px-2.5 py-2">
                      <button
                        type="button"
                        className="font-mono text-[12px] hover:underline"
                        onClick={() => onOpen?.(item.test_case_id)}
                      >
                        {item.test_case_id.slice(0, 8)}
                      </button>
                      <div className="text-[11px] text-muted-foreground">
                        {item.domain ?? "미분류"} · {item.effective_environment}
                      </div>
                      <button
                        type="button"
                        className="text-[11px] text-muted-foreground hover:underline"
                        onClick={() => onOpenResult?.(item.test_case_id)}
                      >
                        Test Result
                      </button>
                    </td>
                    <td className={cn("px-2.5 py-2 font-medium tabular-nums whitespace-nowrap", item.gap != null && item.gap < 0 && "text-muted-foreground")}>
                      {score(item.gap)}
                      {note && <div className="text-[11px] font-normal text-muted-foreground">{note}</div>}
                    </td>
                    <td className="px-2.5 py-2 tabular-nums whitespace-nowrap">
                      {score(item.ref_score)} / {score(item.tc_score)}
                      <div className="text-[11px] text-muted-foreground">{item.rubric_version}</div>
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap">
                      {item.loop_state && (
                        <Badge size="compact" className="border-border">
                          {STATE_LABEL[item.loop_state] ?? item.loop_state}
                        </Badge>
                      )}
                    </td>
                    <td className="px-2.5 py-2 tabular-nums whitespace-nowrap">
                      {item.attempts_counted ?? 0}/{item.attempt_limit}
                      {item.failures ? <div className="text-[11px] text-muted-foreground">실패 {item.failures}</div> : null}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap max-md:hidden">
                      {item.human_version != null ? `v${item.human_version}` : "—"}
                      {item.diverged && (
                        <div className="text-[11px] text-muted-foreground">
                          채점 Reference {item.active_version != null ? `v${item.active_version}` : "없음"}
                        </div>
                      )}
                    </td>
                    <td className="px-2.5 py-2 text-[12px] whitespace-nowrap max-md:hidden">
                      {item.measured_at ? new Date(item.measured_at).toLocaleString("ko-KR") : "—"}
                      {item.tc_run_source && (
                        <div className="text-[11px] text-muted-foreground">
                          {item.tc_run_source === "loop_baseline" ? "루프 기준선" : "배치"} {item.tc_run_git_ref?.slice(0, 8)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {list?.next_cursor && (
          <div className="border-t border-border px-3 py-2.5">
            <Button variant="outline" size="sm" disabled={loadingMore} onClick={() => void more()}>
              {loadingMore && <Loader2 className="animate-spin" />}더 보기
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
